import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  useTheme,
  alpha,
  Chip,
  MenuItem,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useFormikContext } from "formik";
import { BusinessFormValues } from "./BussinessAccounRegister"; // adjust path
import { countryOptions } from "../../../pages/Register/countryCodes";
const denyList = [
  "gmail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "icloud.com",
  "naver.com",
  'yandex.com',
  "qq.com",
  "163.com",
  "126.com",
  "sina.com",
  "sohu.com",
  "aliyun.com",
];

const StepTwoBusinessDetails: React.FC = () => {
  const { values, handleChange, touched, errors, setFieldError, setFieldValue } =
    useFormikContext<BusinessFormValues>();

  const theme = useTheme();
  const error = (k: keyof BusinessFormValues) =>
    touched[k] && errors[k] ? (errors[k] as string) : undefined;

  const GRADIENT = "linear-gradient(90deg, #6366F1 0%, #9F03FF 100%)";
  const GRADIENT_START = "#6366F1";
  const inputBorder = `1px solid ${alpha(GRADIENT_START, 0.25)}`;
  const [showPassword, setShowPassword] = useState(false);

 
  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };
  const handleFieldChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFieldError(name, undefined);
    handleChange(e);
  };

  /**
   * Auto-validate matching domains & block denyList
   */
  useEffect(() => {
    const emailDomain = values.emailDomain.trim().toLowerCase();
    const businessEmail = values.businessEmail.trim().toLowerCase();

    if (emailDomain && businessEmail) {
      const extractedDomain = businessEmail.split("@")[1];

      if (!extractedDomain) return;

      if (denyList.includes(emailDomain) || denyList.includes(extractedDomain)) {
        setFieldError(
          "businessEmail",
          "Public domains like Gmail, Yahoo, Outlook are not allowed."
        );
        setFieldError("emailDomain", "Public domains like Gmail, Yahoo, Outlook are not allowed.");
      } else if (extractedDomain !== emailDomain) {
        setFieldError(
          "businessEmail",
          `Email domain must match "${emailDomain}".`
        );
      } else {
        setFieldError("businessEmail", undefined);
        setFieldError("emailDomain", undefined);
      }
    }
  }, [values.emailDomain, values.businessEmail, setFieldError]);

  return (
    <Box>
      {/* Gradient headline */}
      <Typography
        variant="h6"
        mb={3}
        fontFamily="Lexend, sans-serif"
        fontWeight={700}
        sx={{
          background: GRADIENT,
          WebkitBackgroundClip: "text",
          color: "transparent",
        }}
      >
        2️⃣ Business Details
      </Typography>

      {/* Show selected Business Type */}
      <Box mb={3}>
        <Typography
          variant="subtitle1"
          fontWeight={600}
          mb={1}
          fontFamily="Lexend, sans-serif"
        >
          Selected Business Type:
        </Typography>
        <Chip
          label={values.businessType || "Not Selected"}
          sx={{
            fontFamily: "Lexend, sans-serif",
            fontWeight: 600,
            background: GRADIENT,
            color: "#fff",
            px: 1.5,
            py: 0.5,
            fontSize: "0.9rem",
          }}
        />
      </Box>

      <Grid container spacing={2}>
        {/* Business Name */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Business Name"
            name="businessName"
            value={values.businessName}
            onChange={handleFieldChange}
            error={!!error("businessName")}
            helperText={error("businessName")}
            sx={{ "& fieldset": { border: inputBorder } }}
          />
        </Grid>

        {/* Email Domain */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Email Domain"
            name="emailDomain"
            placeholder="example.com"
            value={values.emailDomain}
            onChange={(e) => {
              handleFieldChange(e);
              // Auto-clean: remove @ if user adds it
              if (e.target.value.startsWith("@")) {
                setFieldValue("emailDomain", e.target.value.slice(1));
              }
            }}
            error={!!error("emailDomain")}
            helperText={error("emailDomain")}
            sx={{ "& fieldset": { border: inputBorder } }}
          />
        </Grid>

        {/* Business Email */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Business Email"
            name="businessEmail"
            placeholder="user@example.com"
            value={values.businessEmail}
            onChange={handleFieldChange}
            error={!!error("businessEmail")}
            helperText={error("businessEmail")}
            sx={{ "& fieldset": { border: inputBorder } }}
          />
        </Grid>

        {/* Password */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type={showPassword ? "text" : "password"}
            label="Password"
            name="password"
            value={values.password}
            onChange={handleFieldChange}
            error={!!error("password")}
            helperText={error("password")}
            sx={{ "& fieldset": { border: inputBorder } }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={togglePasswordVisibility} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {/* Country */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            select
            label="Country"
            name="country"
            value={values.country}
            onChange={handleFieldChange}
            error={!!error("country")}
            helperText={error("country")}
            sx={{ "& fieldset": { border: inputBorder } }}
          >
            {countryOptions.map((country) => (
              <MenuItem key={country.name} value={country.name}>
                {country.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Address */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Address"
            name="address"
            value={values.address}
            onChange={handleFieldChange}
            error={!!error("address")}
            helperText={error("address")}
            sx={{ "& fieldset": { border: inputBorder } }}
          />
        </Grid>

        {/* Contact Name */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Contact Name"
            name="contactName"
            value={values.contactName}
            onChange={handleFieldChange}
            error={!!error("contactName")}
            helperText={error("contactName")}
            sx={{ "& fieldset": { border: inputBorder } }}
          />
        </Grid>

        {/* Contact Country Code */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Contact Country Code"
            name="contactCountryCode"
            value={values.contactCountryCode}
            onChange={handleFieldChange}
            error={!!error("contactCountryCode")}
            helperText={error("contactCountryCode")}
            sx={{ "& fieldset": { border: inputBorder } }}
          />
        </Grid>

        {/* Contact Number */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Contact Number"
            name="contactPhone"
            value={values.contactPhone}
            onChange={handleFieldChange}
            error={!!error("contactPhone")}
            helperText={error("contactPhone")}
            sx={{ "& fieldset": { border: inputBorder } }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default StepTwoBusinessDetails;
